import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { FaFileDownload, FaFilePdf } from "react-icons/fa";
import styles from "./styles.module.scss";
import Skeleton from "../skeleton";
import AssetCard from "../message-ai/components/detail-data/asset-card";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export default function PDFViewer({
  href,
  imageSrc,
  imageAlt,
  title,
  description,
  fullWidth,
}: {
  href: string;
  imageSrc: string;
  imageAlt?: string;
  title?: string;
  description?: string;
  fullWidth?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(720);
  const [loading, setLoading] = useState(true);
  const modalRootRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);

  useEffect(() => {
    modalRootRef.current = document.body;
    
    const otaiElement = document.querySelector("#otai") as HTMLElement;
    if (otaiElement) {
      Modal.setAppElement(otaiElement);
    }
  }, []);

  useEffect(() => {
    const html = document.querySelector("html");

    function handleResize() {
      setContainerWidth(Math.min(html ? html.clientWidth - 40 : 0, 720));
    }

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // containerWidthはresizeのたびに変わるため、レンダリング途中で新しい
    // エフェクトが開始されることがある。各awaitの後にcancelledを確認し、
    // 古いロード/描画処理が新しいものと同じcontainerRef/pdfDocRefを
    // 同時に書き換えてしまう（キャンバスの重複・破損）のを防ぐ。
    let cancelled = false;

    setLoading(true);

    const loadPDF = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: href,
          cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) {
          pdf.destroy();
          return;
        }
        pdfDocRef.current = pdf;
        setLoading(false);

        if (containerRef.current) {
          containerRef.current.innerHTML = '';

          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            if (cancelled) return;
            const page = await pdf.getPage(pageNum);
            if (cancelled) return;
            const viewport = page.getViewport({ scale: 1 });

            const scale = containerWidth / viewport.width;
            const scaledViewport = page.getViewport({ scale });

            const pageDiv = document.createElement('div');
            pageDiv.className = styles.pdfPage;
            pageDiv.style.width = `${scaledViewport.width}px`;
            pageDiv.style.height = `${scaledViewport.height}px`;
            pageDiv.style.marginBottom = '20px';

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            if (!context) continue;

            canvas.width = scaledViewport.width * window.devicePixelRatio;
            canvas.height = scaledViewport.height * window.devicePixelRatio;
            canvas.style.width = `${scaledViewport.width}px`;
            canvas.style.height = `${scaledViewport.height}px`;

            const transform = window.devicePixelRatio !== 1
              ? [window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0]
              : undefined;

            pageDiv.appendChild(canvas);
            containerRef.current?.appendChild(pageDiv);

            await page.render({
              canvasContext: context,
              viewport: scaledViewport,
              transform: transform,
            }).promise;
            if (cancelled) return;
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("[PDFViewer] PDF loading failed:", error);
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [isOpen, href, containerWidth]);

  function handleOpen() {
    setIsOpen(true);
  }

  function handleClose() {
    setIsOpen(false);
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
  }

  function handleDownload(e: React.MouseEvent) {
    e.preventDefault();
    
    const fileName = href.split('/').pop() || 'document.pdf';
    const decodedFileName = decodeURIComponent(fileName);
    
    if (href.includes('s3.amazonaws.com') || href.includes('s3-')) {
      window.open(href, '_blank');
      return;
    }
    
    fetch(href, {
      mode: 'cors',
      credentials: 'omit'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = decodedFileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error("[PDFViewer] Download failed:", error);
        window.open(href, '_blank');
      });
  }

  return (
    <>
      <AssetCard
        thumbnailUrl={imageSrc || undefined}
        alt={imageAlt || "PDF資料"}
        title={title || imageAlt}
        description={description}
        onButtonClick={handleOpen}
        fullWidth={fullWidth}
        fallback={<FaFilePdf size={36} color="#e74c3c" />}
      />
      {modalRootRef.current && (
        <Modal
          className={styles.modal}
          isOpen={isOpen}
          parentSelector={() => modalRootRef.current!}
          onRequestClose={handleClose}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEsc={true}
        >
          <div className={styles.modalHeader}>
            <button 
              onClick={handleDownload} 
              className={styles.downloadButton}
              type="button"
              title="PDFをダウンロード"
            >
              <FaFileDownload className={styles.icon} />
            </button>
          </div>
          <div className={styles.modalContainer}>
            {loading && <Skeleton className={styles.loading} />}
            <div ref={containerRef} className={styles.PDFViewer} />
          </div>
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
